<template>
  <div class="min-h-screen bg-[#FAFAFA] px-4 pt-6 pb-24">
    <!-- Progress Header -->
    <div class="flex items-center justify-between mb-6">
      <button
        class="text-gray-700 text-xl"
        @click="prevPage"
      >
        <i class="fas fa-arrow-left" />
      </button>

      <div class="flex-1 mx-4 h-2 bg-gray-200/80 rounded-full overflow-hidden">
        <div
          class="h-2 bg-[#2563EB] rounded-full transition-all"
          :style="{ width: ((page + 1) / totalPages) * 100 + '%' }"
        />
      </div>

      <span class="text-sm text-gray-500">{{ page + 1 }}/{{ totalPages }}</span>
    </div>

    <!-- Select Subject (Step 1) -->
    <div v-if="page === 0">
      <h2 class="text-lg font-semibold text-gray-900 mb-1">
        {{ t("quizGenerate.subjectStep.title") }}
      </h2>
      <p class="text-sm text-gray-500 mb-4">
        {{ t("quizGenerate.subjectStep.subtitle") }}
      </p>

      <div class="relative mb-6">
        <input
          v-model="search"
          type="text"
          :placeholder="t('quizGenerate.subjectStep.searchPlaceholder')"
          class="w-full px-4 py-3 pl-11 rounded-full border border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
        >
        <i class="fas fa-search absolute left-3.5 top-3.5 text-gray-400" />
        <i class="fas fa-sliders-h absolute right-3.5 top-3.5 text-gray-500" />
      </div>

      <div class="grid grid-cols-2 gap-4 mb-28">
        <div
          v-for="(subject, i) in filteredSubjects"
          :key="i"
          :class="[
            'p-4 rounded-2xl cursor-pointer transition text-center border',
            form.subjectId === subject.SubjectId
              ? 'bg-blue-50/70 border-[#2563EB] shadow'
              : 'bg-white border-gray-100 shadow-sm hover:shadow',
          ]"
          @click="selectSubject(subject)"
        >
          <img
            :src="`/subjects/${subject.SubjectName}.png`"
            alt=""
            class="w-[60px] h-[60px] mx-auto mb-2"
            @error="handleSubjectImgError"
          >
          <h3 class="text-base font-semibold text-gray-900">
            {{ subject.SubjectName }}
          </h3>
          <p class="text-sm text-gray-500 mt-1">
            {{
              subject.Description !== ""
                ? subject.Description
                : t("quizGenerate.subjectStep.fallbackDescription")
            }}
          </p>
        </div>
      </div>
    </div>

    <!-- Timer, Chapter, Level (Step 2) -->
    <div v-else-if="page === 1">
      <div class="space-y-6 mb-28 p-4 rounded-2xl bg-white shadow">
        <!-- Quiz Title -->
        <div>
          <label
            class="text-base font-semibold text-gray-900"
            for="quiz-title"
          >{{ t("quizGenerate.setupStep.titleLabel") }}</label>
          <input
            id="quiz-title"
            v-model="form.quizTitle"
            type="text"
            :placeholder="t('quizGenerate.setupStep.titlePlaceholder')"
            class="w-full mt-2 px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
          >
        </div>

        <!-- Description -->
        <div>
          <label
            class="text-base font-semibold text-gray-900"
            for="quiz-description"
          >{{ t("quizGenerate.setupStep.descriptionLabel") }}</label>
          <textarea
            id="quiz-description"
            v-model="form.description"
            :placeholder="t('quizGenerate.setupStep.descriptionPlaceholder')"
            rows="4"
            class="w-full mt-2 px-4 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm focus:ring-2 focus:ring-[#2563EB] focus:border-transparent resize-none"
          />
        </div>

        <!-- Timer -->
        <div>
          <label class="text-base font-semibold text-gray-900">{{
            t("quizGenerate.setupStep.timerLabel")
          }}</label>
          <input
            v-model.number="form.timerIndex"
            type="range"
            min="0"
            max="60"
            step="1"
            class="slider"
            :style="{ '--val': `${percent}%` }"
          >
          <div
            class="flex font-manrope justify-between items-center text-base mt-1 text-gray-500 px-0.5"
          >
            <span>{{ t("quizGenerate.setupStep.timerMin") }}</span>
            <span class="text-gray-900 font-semibold">{{ timerOptions[form.timerIndex].value }}m</span>
            <span>{{ t("quizGenerate.setupStep.timerMax") }}</span>
          </div>
        </div>

        <!-- Chapter -->
        <div>
          <label class="text-base font-semibold text-gray-900">{{
            t("quizGenerate.setupStep.chapterLabel")
          }}</label>
          <div class="relative">
            <DrawerSelect
              v-model="form.chapter"
              :options="chapterDrawerOptions"
              :title="t('quizGenerate.drawer.chapterTitle')"
              :placeholder="t('quizGenerate.drawer.chapterPlaceholder')"
            />
          </div>
        </div>

        <!-- Add Option -->
        <div>
          <label class="text-base font-semibold text-gray-900">{{ t("quizGenerate.drawer.confirmPinInputTitle") }}</label>
          <div class="relative">
            <DrawerSelect
              v-model="form.confirmationPinInput"
              :options="confirmPinInput"
              :title="t('quizGenerate.drawer.confirmPinInputTitle')"
              :placeholder="t('quizGenerate.drawer.confirmPinInputTitle')"
            />
          </div>
          <div v-if="isPinEnabled">
            <PinInput
              v-model="form.pin"
              class="my-5"
            />
          </div>
        </div>

        <!-- Grade -->
        <div>
          <label class="text-base font-semibold text-gray-900">{{
            t("quizGenerate.setupStep.gradeLabel")
          }}</label>
          <div class="relative">
            <DrawerSelect
              v-model="form.gradeId"
              :options="allGrade"
              label-key="GradeName"
              value-key="Id"
              :title="t('quizGenerate.drawer.gradeTitle')"
              :placeholder="t('quizGenerate.drawer.gradePlaceholder')"
            />
          </div>
        </div>

        <!-- Level -->
        <!-- <div>
          <label class="text-base font-semibold text-gray-900">Level</label>
          <input
            v-model="form.level"
            type="range"
            min="0"
            max="4"
            step="1"
            class="block w-full py-2 mt-2 text-gray-700 bg-white border border-gray-300 rounded-md focus:border-blue-500 focus:outline-none focus:ring"
          />
          <div class="flex justify-between text-base text-gray-500 mt-1">
            <span>Easy</span>
            <span>Difficult</span>
          </div>
        </div> -->

        <!-- Total Question -->
        <div>
          <label class="text-base font-semibold text-gray-900">{{
            t("quizGenerate.setupStep.totalQuestionLabel")
          }}</label>
          <div class="relative">
            <select
              v-model="form.totalQuestion"
              class="w-full mt-2 pl-4 pr-10 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm appearance-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
            >
              <option>10</option>
              <option>20</option>
              <option>30</option>
              <option>40</option>
              <option>50</option>
            </select>
            <i
              class="fas fa-chevron-down absolute right-3 top-[60%] -translate-y-1/2 text-gray-400"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Cover (Step 3) -->
    <div v-else-if="page === 2">
      <div class="mt-6 bg-white p-4 rounded-2xl shadow">
        <h2 class="text-base font-semibold text-gray-900 mb-4">
          {{ t("quizGenerate.coverStep.title") }}
        </h2>

        <div class="grid grid-cols-3 gap-3 mb-6">
          <div
            v-for="(img, i) in allCovers"
            :key="i"
            class="relative rounded-xl overflow-hidden cursor-pointer"
            @click="form.coverImage = img"
          >
            <img
              :src="img"
              class="w-full h-24 object-cover"
              :class="form.coverImage === img ? 'ring-2 ring-blue-500' : ''"
            >
            <div
              v-if="form.coverImage === img"
              class="absolute inset-0 bg-black/30 flex items-center justify-center"
            >
              <div
                class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs"
              >
                ✓
              </div>
            </div>
          </div>
        </div>

        <label
          class="w-full flex items-center justify-center gap-2 border border-blue-600 text-blue-600 py-3 rounded-full font-medium text-sm cursor-pointer"
        >
          <i class="fas fa-folder-open" />
          {{ t("quizGenerate.coverStep.uploadButton") }}
          <input
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleFileUpload"
          >
        </label>
      </div>
    </div>

    <!-- Privacy (Step 4) -->
    <div v-else-if="page === 3">
      <div class="mb-28 mt-5 bg-white rounded-2xl p-4 shadow">
        <h2 class="text-md font-semibold text-gray-900 mb-4">
          {{ t("quizGenerate.privacyStep.title") }}
        </h2>
        <p class="text-sm text-gray-500 mb-4">
          {{ t("quizGenerate.privacyStep.subtitle") }}
        </p>

        <div class="space-y-4">
          <!-- Private -->
          <label
            class="flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition"
            :class="
              form.privacy === 'private'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white'
            "
          >
            <input
              v-model="form.privacy"
              type="radio"
              value="private"
              class="hidden"
            >
            <div class="mt-1 text-xl"><i class="fas fa-lock" /></div>
            <div class="flex-1">
              <div class="font-semibold text-gray-900">
                {{ t("quizGenerate.privacyStep.privateTitle") }}
                <span class="text-xs text-gray-400 ml-1">{{
                  t("quizGenerate.privacyStep.privateBadge")
                }}</span>
              </div>
              <p class="text-sm text-gray-500">
                {{ t("quizGenerate.privacyStep.privateDescription") }}
              </p>
            </div>
            <div class="mt-1">
              <span
                class="w-5 h-5 inline-block rounded-full border-2 flex items-center justify-center"
                :class="
                  form.privacy === 'private'
                    ? 'border-blue-500'
                    : 'border-gray-300'
                "
              >
                <span
                  class="w-2.5 h-2.5 rounded-full"
                  :class="form.privacy === 'private' ? 'bg-blue-500' : ''"
                />
              </span>
            </div>
          </label>

          <!-- Public -->
          <label
            class="flex items-start gap-3 p-3 rounded-2xl border cursor-pointer transition"
            :class="
              form.privacy === 'public'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white'
            "
            @click="checkPublic()"
          >
            <input
              v-model="form.privacy"
              type="radio"
              value="public"
              class="hidden"
            >
            <div class="mt-1 text-xl"><i class="fas fa-globe" /></div>
            <div class="flex-1">
              <div class="font-semibold text-gray-900">
                {{ t("quizGenerate.privacyStep.publicTitle") }}
                <span class="text-xs text-red-500 italic ml-1">{{
                  t("quizGenerate.privacyStep.publicBadge")
                }}</span>
              </div>
              <p class="text-sm text-gray-500">
                {{ t("quizGenerate.privacyStep.publicDescription") }}
              </p>
            </div>
            <div class="mt-1">
              <span
                class="w-5 h-5 inline-block rounded-full border-2 flex items-center justify-center"
                :class="
                  form.privacy === 'public'
                    ? 'border-blue-500'
                    : 'border-gray-300'
                "
              >
                <span
                  class="w-2.5 h-2.5 rounded-full"
                  :class="form.privacy === 'public' ? 'bg-blue-500' : ''"
                />
              </span>
            </div>
          </label>
        </div>
      </div>

      <!-- Subscribe modal -->
      <div
        v-if="showModal"
        class="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
      >
        <div
          class="bg-white w-80 rounded-2xl text-center shadow-lg overflow-hidden"
        >
          <div
            class="bg-[#FFF5E5] flex items-center justify-center py-6 relative"
          >
            <img
              src="/modals/crown.png"
              alt="Premium Icon"
              class="w-14 h-14"
            >
            <div
              class="absolute left-6 top-6 w-2 h-2 rounded-full bg-orange-400"
            />
            <div
              class="absolute right-6 top-8 w-2 h-2 rounded-full bg-orange-400"
            />
            <div
              class="absolute left-10 bottom-5 w-2 h-2 rounded-full bg-orange-400"
            />
            <div
              class="absolute right-10 bottom-5 w-2 h-2 rounded-full bg-orange-400"
            />
          </div>

          <div class="px-6 py-5">
            <h3 class="font-semibold text-gray-900 mb-2">
              {{ t("quizGenerate.modal.title") }}
            </h3>
            <p class="text-sm text-gray-500 mb-6">
              {{ t("quizGenerate.modal.descriptionBefore") }}
              <span class="text-red-500 font-semibold">{{
                t("quizGenerate.modal.descriptionHighlight")
              }}</span>
              {{ t("quizGenerate.modal.descriptionAfter") }}
            </p>
            <div class="space-y-3">
              <button
                class="w-full bg-blue-600 text-white py-2 rounded-full font-semibold"
                @click="subscribeNow"
              >
                {{ t("quizGenerate.modal.subscribeNow") }}
              </button>
              <button
                class="w-full border border-blue-600 text-blue-600 py-2 rounded-full font-semibold"
                @click="maybeLater"
              >
                {{ t("quizGenerate.modal.maybeLater") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Nav Buttons -->
    <div
      class="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex gap-3"
    >
      <button
        class="border border-[#2563EB] text-[#2563EB] w-full py-3 rounded-[20px] font-semibold"
        @click="prevPage"
      >
        {{ t("quizGenerate.buttons.back") }}
      </button>
      <button
        class="bg-[#0B63F6] hover:bg-[#155EE0] text-white w-full py-3 rounded-[20px] font-semibold"
        @click="nextPage"
      >
        {{
          isLastPage
            ? t("quizGenerate.buttons.create")
            : t("quizGenerate.buttons.next")
        }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { QuizListSubject } from "@/api/subjectApi";
import { ref, computed, reactive, watchEffect, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import { useAuthStore } from "@/stores/authStore";
import { useRouter } from "vue-router";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { QuizGradeAll } from "@/api/quizApi";
import DrawerSelect from "@/components/base/DrawerSelect.vue";
import { getKiddoBaseUrl } from "@/api/httpClient";
import PinInput from "@/components/base/PinInput.vue";
import noImage from "@/assets/no-image.jpg";

const max = 60;
const percent = computed(() =>
  Math.max(0, Math.min(100, (Number(form.timerIndex) / max) * 100))
);

const snackbar = useSnackbarStore();
const router = useRouter();
const auth = useAuthStore();
const { t } = useI18n();

const showModal = ref(false);
const uploadedFile = ref(null);
const uploadedCover = ref(null);

function checkPublic() {
  const isSubscribed = auth.userId === 37 || auth.userId === 27 || auth.userId === 185 ? true : false;
  if (!isSubscribed) {
    showModal.value = true;
    form.privacy = "private";
  }
}
function subscribeNow() {
  showModal.value = false;
  router.push("/subscribe");
}

function maybeLater() {
  form.privacy = "private"; // force back to private
  showModal.value = false;
}

const form = reactive({
  subjectId: null,
  subjectName: "",
  quizTitle: "",
  description: "",
  timerIndex: 1, // 0=1m, 1=30m, 2=60m
  timer: 30,
  chapter: "1,2,3,4,5,6,7,8,9",
  level: 2, // 0..4
  totalQuestion: 10,
  confirmationPinInput: "0",
  pin: "",
  privacy: "private",
  coverImage: "",
  points: 5,
  gradeId: "",
});
const isPinEnabled = computed(() => form.confirmationPinInput === "1");

watch(
  () => form.confirmationPinInput,
  (value) => {
    if (value !== "1") {
      form.pin = "";
    }
  }
);

const timerOptions = computed(() =>
  Array.from({ length: 60 }, (_, i) => ({
    label: t("quizGenerate.timer.option", { count: i + 1 }),
    value: i + 1,
  }))
);
watchEffect(() => {
  const currentOption = timerOptions.value[form.timerIndex];
  if (currentOption) form.timer = currentOption.value;
});

const page = ref(0);
const totalPages = 4;
const search = ref("");
const allSubjects = ref([]);
const allGrade = ref([]);
const subjects = ref([]);

const chapterDrawerOptions = computed(() => [
  {
    label: t("quizGenerate.drawer.chapterPlaceholder"),
    value: "1,2,3,4,5,6,7,8,9",
  },
]);

const confirmPinInput = computed(() => [
  { label: t("quizGenerate.drawer.optionTrue"), value: "1" },
  { label: t("quizGenerate.drawer.optionFalse"), value: "0" },
]);

const quizCovers = ref([
  "/covers/cover1.png",
  "/covers/cover2.png",
  "/covers/cover3.jpg",
  "/covers/cover4.jpg",
  "/covers/cover5.jpg",
  "/covers/cover6.jpg",
  "/covers/cover7.jpg",
  "/covers/cover8.jpg",
  "/covers/cover9.jpg",
]);

const allCovers = computed(() =>
  uploadedCover.value
    ? [uploadedCover.value, ...quizCovers.value]
    : quizCovers.value
);

// Default the cover to the first available option when empty
watchEffect(() => {
  if (!form.coverImage && allCovers.value.length) {
    form.coverImage = allCovers.value[0];
  }
});

function handleFileUpload(event) {
  const file = event.target.files[0];
  if (file) {
    uploadedFile.value = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      uploadedCover.value = e.target.result;
      form.coverImage = uploadedCover.value;
    };
    reader.readAsDataURL(file);
  }
}

const getAllSubject = async () => {
  try {
    const data = await QuizListSubject();
    allSubjects.value = data;
    subjects.value = data;
  } catch (error) {
    console.error(error);
  }
};
const getAllGrade = async () => {
  try {
    const data = await QuizGradeAll();
    allGrade.value = data;
  } catch (error) {
    console.error(error);
  }
};
onMounted(async () => {
  getAllGrade();
  getAllSubject();
});

const selectSubject = (subject) => {
  form.subjectId = subject.SubjectId;
  form.subjectName = subject.SubjectName;
};

const handleSubjectImgError = (event) => {
  const img = event?.target;
  if (!img) return;
  img.onerror = null; // prevent loops if fallback fails
  img.src = noImage;
};
const filteredSubjects = computed(() =>
  subjects.value.filter((s) =>
    s.SubjectName.toLowerCase().includes(search.value.toLowerCase())
  )
);

const isFirstPage = computed(() => page.value === 0);
const isLastPage = computed(() => page.value === totalPages - 1);

const submitQuiz = async () => {
  const baseURL = await getKiddoBaseUrl();
  try {
    const formData = new FormData();
    formData.append("userId", auth.userId);
    formData.append("subjectId", form.subjectId);
    formData.append("quizTitle", form.quizTitle.trim());
    formData.append(
      "description",
      form.description ? form.description.trim() : ""
    );
    formData.append("gradeId", form.gradeId);
    formData.append("timer", toHHMMSS(form.timer));
    formData.append("chapter", form.chapter);
    formData.append("difficulty", generateDifficulty(form.level));
    formData.append("totalQuestion", form.totalQuestion);
    formData.append("addPinWhenReview", isPinEnabled.value ? "true" : "false");
    if (isPinEnabled.value && form.pin) {
      formData.append("reviewPin", form.pin);
    } else {
      formData.append("reviewPin", "");
    }
    formData.append("points", form.points.toString());
    formData.append("isPublic", form.privacy === "public");
    if (uploadedFile.value) {
      formData.append("coverImage", uploadedFile.value);
    } else if (form.coverImage) {
      try {
        const response = await fetch(form.coverImage);
        const blob = await response.blob();
        const filename =
          (form.coverImage.split("/").pop() || "cover.jpg").split("?")[0];
        const file = new File([blob], filename, {
          type: blob.type || "image/jpeg",
        });
        formData.append("coverImage", file);
      } catch (err) {
        console.warn("Failed to attach cover image, proceeding without it", err);
      }
    }

    const response = await axios.post(
      `${baseURL}/insert/GenerateRandomQuestion`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );

    console.log("SUCCESS:", response.data);
    snackbar.trigger(
      t("quizGenerate.snackbar.createSuccess", { subject: form.subjectName }),
      "success"
    );
    router.push("/quiz");
  } catch (error) {
    const backendMessage = error?.response?.data?.message;
    const message = backendMessage
      ? t("quizGenerate.snackbar.createFailedWithReason", {
          reason: backendMessage,
        })
      : t("quizGenerate.snackbar.createFailed");
    snackbar.trigger(message, "error");
    console.error("FAILED:", error);
  }
};

const toHHMMSS = (minute) => {
  const m = String(minute).padStart(2, "0");
  return `00:${m}:00`;
};
const generateDifficulty = (level) => {
  switch (parseInt(level)) {
    case 0:
      return "1";
    case 1:
      return "1,2";
    case 2:
      return "1,2,3";
    case 3:
      return "2,3,4";
    case 4:
      return "3,4,5";
    default:
      return "1,2,3";
  }
};

function validateStep(step) {
  switch (step) {
    case 0:
      if (!form.subjectId) {
        snackbar.trigger(t("quizGenerate.snackbar.selectSubject"), "error");
        return false;
      }
      return true;
    case 1:
      if (!form.quizTitle || !form.quizTitle.trim()) {
        snackbar.trigger(t("quizGenerate.snackbar.titleRequired"), "error");
        return false;
      }
      if (!form.gradeId) {
        snackbar.trigger(t("quizGenerate.snackbar.gradeRequired"), "error");
        return false;
      }
      if (!form.totalQuestion || Number(form.totalQuestion) <= 0) {
        snackbar.trigger(
          t("quizGenerate.snackbar.totalQuestionRequired"),
          "error"
        );
        return false;
      }
      if (isPinEnabled.value && !/^\d{4}$/.test(form.pin || "")) {
        snackbar.trigger(t("quizGenerate.snackbar.pinRequired"), "error");
        return false;
      }
      return true;
    case 2:
      // Cover optional; keep as valid
      return true;
    case 3:
      if (!form.privacy) {
        snackbar.trigger(t("quizGenerate.snackbar.privacyRequired"), "error");
        return false;
      }
      return true;
    default:
      return true;
  }
}

const nextPage = () => {
  if (!validateStep(page.value)) return;
  if (!isLastPage.value) page.value++;
  else {
    // Final validation before submit
    if (validateStep(3)) submitQuiz();
  }
};
const prevPage = () => {
  if (isFirstPage.value) {
    router.push("/home");
  } else {
    page.value--;
  }
};
</script>

<style scoped>
/* Slider style (works on WebKit + Firefox) */
.range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 10px;
  border-radius: 9999px;
  /* progress bg */
  background: linear-gradient(#2563eb, #2563eb) no-repeat, #e5e7eb;
  background-size: var(--percent, 0%) 100%;
  outline: none;
}

/* WebKit Thumb */
.range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: #2563eb;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
  margin-top: -6px; /* centers thumb on track */
  cursor: pointer;
}

/* Firefox Track/Thumb */
.range::-moz-range-track {
  height: 10px;
  border: none;
  border-radius: 9999px;
  background: transparent;
}
.range::-moz-range-progress {
  height: 10px;
  background: #2563eb;
  border-radius: 9999px;
}
.range::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: #2563eb;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.35);
  cursor: pointer;
}

/* (Optional) improve selects on iOS/Safari */
select {
  -webkit-appearance: none;
  background-image: none;
}
</style>
